import FormSelect from 'components/Select/FormSelect';
import Text from 'components/Text/Text';
import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { IDishesByRestaurantIdsAndSubcategoryIdsItem } from 'types';
import { Checkbox } from 'antd';
import SearchField from 'components/SearchField/SearchField';
import AdditionalInfoList from 'components/Select/AdditionalInfoList';
import ChunkOfTextWithCopying from 'components/Text/ChunkOfTextWithCopying';
import style from './MultipleSelect.module.scss';

interface IMultipleSelect {
	data: IDishesByRestaurantIdsAndSubcategoryIdsItem[];
	isLoading: boolean;
	refetch: () => void;
	responseError: boolean;
	setIDsDishes: (value: string[]) => void;
	IDsSelectedSubcategories: string[];
	IDsSelectedDishes: string[];
}

const MultipleSelect: React.FC<IMultipleSelect> = ({
	data,
	isLoading,
	refetch,
	responseError,
	IDsSelectedSubcategories,
	IDsSelectedDishes,
	setIDsDishes,
}) => {
	const [searchString, setSearchString] = useState<string>('');

	const filteredDataWithAdditionalContent = useMemo(() => {
		const updatedData = data.map((el) => ({
			...el,
			additionalContent: (
				<>
					{(el.subCategoryNames || el.categoryNames || el.externalId) && (
						<div className={style.additionalInfo}>
							{el.subCategoryNames && (
								<div className={style.infoItem}>
									<AdditionalInfoList
										data={el.subCategories.map(
											(el) => `${el.subCategoryName} / ${el.categoryName}`,
										)}
										singularTranslationKey="singularSubcategoryLabel"
										pluralTranslationKey="pluralSubcategoriesLabel"
									/>
								</div>
							)}
							<div className={style.externalIdWrapper}>
								{el.externalId && (
									<ChunkOfTextWithCopying text={el.externalId} className={style.externalId} />
								)}
							</div>
						</div>
					)}
				</>
			),
		}));

		if (searchString) {
			const filteredDataByName = updatedData.filter((el) =>
				el.name.toLowerCase().includes(searchString.toLowerCase()),
			);
			const filteredDataByExternalId = updatedData.filter((el) =>
				el.externalId?.toLowerCase().includes(searchString.toLowerCase()),
			);
			const unionFilteredData = Array.from(new Set([...filteredDataByName, ...filteredDataByExternalId]));
			return unionFilteredData;
		}
		return updatedData;
	}, [searchString, data]);

	const handleSelectAllData = useCallback(() => {
		if (data.length === IDsSelectedDishes.length) {
			setIDsDishes([]);
		} else {
			const IDsOfAllDishes = data.map((el) => el.id);
			setIDsDishes(IDsOfAllDishes);
		}
	}, [data, IDsSelectedDishes, setIDsDishes]);

	useEffect(() => {
		setSearchString('');
	}, [IDsSelectedSubcategories]);

	const renderSelectMenu = useCallback(
		(menu: React.ReactElement) => {
			return (
				<>
					<div className={style.selectMenuHeader}>
						<Checkbox
							className={style.checkBox}
							checked={!!(data.length && data.length === IDsSelectedDishes.length)}
							disabled={!data.length}
							onChange={handleSelectAllData}>
							<Text translationNameSpace="form" translationKey="selectAllDishesLabel" asFragment={true} />
						</Checkbox>
						<SearchField
							onChangeSelectSearchString={setSearchString}
							searchString={searchString}
							className={style.searchField}
						/>
					</div>
					{menu}
				</>
			);
		},
		[data, IDsSelectedDishes, handleSelectAllData, setSearchString, searchString],
	);

	return (
		<FormSelect
			name="IDsSelectedDishes"
			label="dishesLabel"
			options={filteredDataWithAdditionalContent}
			fetchError={responseError}
			loading={isLoading}
			onRefetch={refetch}
			disabled={!IDsSelectedSubcategories.length || isLoading}
			onChange={setIDsDishes}
			counterValue={IDsSelectedDishes.length}
			dropdownRender={renderSelectMenu}
			mode="multiple"
		/>
	);
};

export default React.memo(MultipleSelect);
